/**
 * ArticleIndexSettings — inline editor of an `ArticleIndex` section (article documents only).
 *
 * UI only: the folder / topic lists come in as props (useArticleIndexOptions is the bridge to the
 * firebase-core services), every change calls `onChange(nextContent)` with the FULL
 * `articleIndexContent` object (flexible-layout Rule 1: the caller writes the whole sections array).
 * Layout and copy are the owner-approved mock (articles-path-mode, C-22).
 *
 * Authoring contract (the article validator must accept what this writes):
 *  - the heading is edited per `locale`; other languages are kept as they are;
 *  - the filter is one-sided: "Folder" writes { folderId } and drops a topic, "Topic" the reverse,
 *    "All articles" removes the `filter` key; a kind without a pick yet writes NO filter (an empty
 *    id would be refused), the pending kind is UI state only;
 *  - `limit` is an integer clamped to 1–50.
 *
 * StyledTextField / StyledSelectDropdown are memoised and ignore changes of `onChange`, so every
 * callback below reads the latest props through refs.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material';
import type { ArticleIndexContent, LayoutSection } from '@segmently/schema-models/v2';
// Subpath import: the @modules/ui-components root barrel is not jest-safe (import.meta via firebase-core).
import { StyledSelectDropdown, StyledTextField } from '@modules/ui-components/forms/fields';
import { ARTICLE_INDEX_TEST_IDS } from './articleIndexTestIds';

export interface ArticleIndexFolderOption { id: string; name: string }
export interface ArticleIndexTopicOption { id: string; label: string }

export interface ArticleIndexSettingsProps {
  section: LayoutSection;
  onChange: (content: ArticleIndexContent) => void;
  folders: ArticleIndexFolderOption[];
  topics: ArticleIndexTopicOption[];
  /** Editing locale — the language of the Heading field */
  locale: string;
  /** Folder / topic lists are still loading */
  loading?: boolean;
  disabled?: boolean;
}

type FilterKind = 'all' | 'folder' | 'topic';
type ToggleName = 'showThumbnail' | 'showTopic' | 'showDate' | 'showDescription';
type SortValue = NonNullable<ArticleIndexContent['sort']>;

export const ARTICLE_INDEX_LIMIT = { min: 1, max: 50, default: 12 } as const;

const FILTER_OPTIONS: Array<{ value: FilterKind; label: string }> = [
  { value: 'all', label: 'All articles' },
  { value: 'folder', label: 'Articles in a folder' },
  { value: 'topic', label: 'Articles on a topic' },
];

const SORT_OPTIONS: Array<{ value: SortValue; label: string }> = [
  { value: 'dateModified', label: 'Last updated' },
  { value: 'datePublished', label: 'First published' },
  { value: 'title', label: 'Title (A–Z)' },
];

const TOGGLES: Array<{ name: ToggleName; label: string }> = [
  { name: 'showThumbnail', label: 'Cover image' },
  { name: 'showTopic', label: 'Topic' },
  { name: 'showDate', label: 'Date' },
  { name: 'showDescription', label: 'Description' },
];

const filterKeyOf = (filter: ArticleIndexContent['filter']): string =>
  filter?.folderId ? `folder:${filter.folderId}` : filter?.topicId ? `topic:${filter.topicId}` : '';

const kindOf = (filter: ArticleIndexContent['filter']): FilterKind =>
  filter?.folderId ? 'folder' : filter?.topicId ? 'topic' : 'all';

/** Integer inside 1–50, or null when the text is not a number. */
export function parseArticleIndexLimit(text: string): number | null {
  const parsed = parseInt(text, 10);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(ARTICLE_INDEX_LIMIT.max, Math.max(ARTICLE_INDEX_LIMIT.min, parsed));
}

/** Without the saved id in the list a select would show nothing — keep an orphaned pick visible. */
function withSavedOption(options: Array<{ value: string; label: string }>, savedId: string | undefined) {
  return savedId && !options.some((option) => option.value === savedId) ? [...options, { value: savedId, label: savedId }] : options;
}

export const ArticleIndexSettings: React.FC<ArticleIndexSettingsProps> = ({
  section,
  onChange,
  folders,
  topics,
  locale,
  loading = false,
  disabled = false,
}) => {
  const content = useMemo<ArticleIndexContent>(
    () => section.articleIndexContent ?? { heading: {} },
    [section.articleIndexContent],
  );

  // Latest props for the memoised fields below.
  const contentRef = useRef(content);
  contentRef.current = content;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const localeRef = useRef(locale);
  localeRef.current = locale;

  const emit = useCallback((patch: Partial<ArticleIndexContent>, dropFilter = false) => {
    const { filter, ...rest } = contentRef.current;
    const next: ArticleIndexContent = { ...rest, ...(dropFilter ? {} : filter ? { filter } : {}), ...patch };
    onChangeRef.current(next);
  }, []);

  // ── Filter ────────────────────────────────────────────────────────────
  // A chosen kind with no folder/topic picked yet is not storable (an empty id is invalid), so it lives here. It is
  // bound to the section and to the stored filter it was chosen on top of (always "no filter" once the old one is
  // dropped): an external change — undo, another section — makes it stale, and the stored filter wins again.
  const [pending, setPending] = useState<{ kind: FilterKind; sectionId: string; filterKey: string } | null>(null);
  const filterKey = filterKeyOf(content.filter);
  const pendingKind = pending && pending.sectionId === section.id && pending.filterKey === filterKey ? pending.kind : null;
  const filterKind = pendingKind ?? kindOf(content.filter);
  const sectionIdRef = useRef(section.id);
  sectionIdRef.current = section.id;

  const handleKind = useCallback((value: string | number | (string | number)[]) => {
    const kind = value as FilterKind;
    const stored = contentRef.current.filter;
    if (kind === kindOf(stored)) {
      setPending(null);
      return;
    }
    setPending(kind === 'all' ? null : { kind, sectionId: sectionIdRef.current, filterKey: '' });
    if (stored) emit({}, true); // nothing stored → nothing to write (no dirty / undo entry)
  }, [emit]);

  const handleFolder = useCallback((value: string | number | (string | number)[]) => {
    setPending(null);
    emit({ filter: { folderId: String(value) } });
  }, [emit]);

  const handleTopic = useCallback((value: string | number | (string | number)[]) => {
    setPending(null);
    emit({ filter: { topicId: String(value) } });
  }, [emit]);

  const folderOptions = useMemo(
    () => withSavedOption(folders.map((folder) => ({ value: folder.id, label: folder.name })), content.filter?.folderId),
    [folders, content.filter?.folderId],
  );
  const topicOptions = useMemo(
    () => withSavedOption(topics.map((topic) => ({ value: topic.id, label: topic.label })), content.filter?.topicId),
    [topics, content.filter?.topicId],
  );

  // ── Sort / heading ────────────────────────────────────────────────────
  const handleSort = useCallback((value: string | number | (string | number)[]) => emit({ sort: value as SortValue }), [emit]);

  const handleHeading = useCallback((text: string) => {
    emit({ heading: { ...contentRef.current.heading, [localeRef.current]: text } });
  }, [emit]);

  // ── Limit: a text buffer so the field can be emptied while typing a new number ──
  const storedLimit = content.limit ?? ARTICLE_INDEX_LIMIT.default;
  const [limitText, setLimitText] = useState(String(storedLimit));
  useEffect(() => {
    setLimitText((current) => (parseArticleIndexLimit(current) === storedLimit ? current : String(storedLimit)));
  }, [storedLimit]);

  const handleLimit = useCallback((text: string) => {
    const limit = parseArticleIndexLimit(text);
    if (limit === null) {
      setLimitText(text);
      return;
    }
    // A value outside 1–50 snaps to the bound at once; an in-range value keeps what the author typed.
    setLimitText(limit === parseInt(text, 10) ? text : String(limit));
    emit({ limit });
  }, [emit]);

  const handleLimitBlur = useCallback(() => {
    setLimitText(String(contentRef.current.limit ?? ARTICLE_INDEX_LIMIT.default));
  }, []);

  return (
    <Box data-testid={ARTICLE_INDEX_TEST_IDS.root} sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 1 }}>
      <Typography variant="subtitle2" component="h6">Article index</Typography>

      <StyledTextField
        data-testid={ARTICLE_INDEX_TEST_IDS.heading}
        label="Heading"
        helperText="Shown above the list. Leave empty for no heading."
        value={content.heading?.[locale] ?? ''}
        onChange={handleHeading}
        disabled={disabled}
        size="small"
        fullWidth
      />

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StyledSelectDropdown
            data-testid={ARTICLE_INDEX_TEST_IDS.filterKind}
            label="Show"
            value={filterKind}
            onChange={handleKind}
            options={FILTER_OPTIONS}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
        {filterKind === 'folder' && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StyledSelectDropdown
              data-testid={ARTICLE_INDEX_TEST_IDS.folderSelect}
              label="Folder"
              value={content.filter?.folderId ?? ''}
              onChange={handleFolder}
              options={folderOptions}
              loading={loading}
              disabled={disabled}
              size="small"
              fullWidth
            />
          </Box>
        )}
        {filterKind === 'topic' && (
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <StyledSelectDropdown
              data-testid={ARTICLE_INDEX_TEST_IDS.topicSelect}
              label="Topic"
              value={content.filter?.topicId ?? ''}
              onChange={handleTopic}
              options={topicOptions}
              loading={loading}
              disabled={disabled}
              size="small"
              fullWidth
            />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StyledSelectDropdown
            data-testid={ARTICLE_INDEX_TEST_IDS.sortSelect}
            label="Sort by"
            value={content.sort ?? 'dateModified'}
            onChange={handleSort}
            options={SORT_OPTIONS}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <StyledTextField
            data-testid={ARTICLE_INDEX_TEST_IDS.limitInput}
            label="Max articles"
            helperText="1–50"
            type="number"
            value={limitText}
            onChange={handleLimit}
            onBlur={handleLimitBlur}
            disabled={disabled}
            size="small"
            fullWidth
          />
        </Box>
      </Box>

      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Show for each article</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, mt: 0.5 }}>
          {TOGGLES.map(({ name, label }) => (
            <FormControlLabel
              key={name}
              label={<Typography variant="body2">{label}</Typography>}
              control={
                // Standard MUI checkbox, as in the approved mock: the editor's compact style-row checkbox has no
                // padding, so inside a FormControlLabel (-11px start margin) it hung outside the panel edge.
                <Checkbox
                  checked={Boolean(content[name])}
                  onChange={(event) => emit({ [name]: event.target.checked })}
                  disabled={disabled}
                  size="small"
                  inputProps={{ 'aria-label': label, 'data-testid': ARTICLE_INDEX_TEST_IDS.toggle(name) } as React.InputHTMLAttributes<HTMLInputElement>}
                />
              }
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          The title link is always shown. The list is built from your live articles when you publish.
        </Typography>
      </Box>
    </Box>
  );
};
